const DEFAULT_ALIASES = {
  'make': ['make', 'make com', 'integromat'],
  'make com': ['make', 'make com', 'integromat'],
  'copy ai': ['copy ai', 'copy.ai'],
  'copy.ai': ['copy ai', 'copy.ai'],
  'meta ads api': ['meta ads', 'facebook ads', 'meta ads api'],
  'chatgpt team': ['chatgpt', 'chat gpt', 'chatgpt team', 'chatgpt.com', 'openai chatgpt'],
  'chatgpt': ['chatgpt', 'chat gpt', 'chatgpt team', 'chatgpt.com', 'openai chatgpt'],
  'claude': ['claude', 'claude sonnet', 'anthropic claude'],
  'zapier': ['zapier', 'zapier.com'],
  'claude sonnet': ['claude', 'claude sonnet', 'anthropic claude'],
  'quickbooks': ['quickbooks', 'quickbooks online', 'qb'],
  'qb': ['quickbooks', 'quickbooks online', 'qb'],
  'servicetitan': ['service titan', 'servicetitan'],
  'service titan': ['service titan', 'servicetitan'],
  'power bi': ['power bi', 'microsoft power bi'],
  'looker studio': ['looker studio', 'google data studio', 'data studio'],
  'jobber': ['jobber'],
  'hubspot': ['hubspot', 'hubspot crm'],
  'hubspot crm': ['hubspot', 'hubspot crm'],
  'data studio': ['looker studio', 'google data studio', 'data studio'],
  'document ai': ['document ai', 'google document ai'],
  'notion ai': ['notion', 'notion ai'],
  'intercom fin': ['intercom', 'intercom fin'],
  'twilio': ['twilio'],
};

export const normalizeToolName = (value = '') =>
  String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const GENERIC_TOKENS = new Set([
  'ai',
  'api',
  'app',
  'bot',
  'engine',
  'platform',
  'service',
  'services',
  'solutions',
  'studio',
  'system',
  'team',
  'tool',
  'tools',
]);

const toTokenSet = (value = '') => {
  const tokens = normalizeToolName(value)
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && !GENERIC_TOKENS.has(token));
  return new Set(tokens);
};

export const extractStepCandidateGroups = (toolName = '', aliases = DEFAULT_ALIASES) => {
  const normalized = String(toolName || '').replace(/\(.*?\)/g, ' ').trim();
  if (!normalized) return [];

  return normalized
    .split('+')
    .map((group) => group.trim())
    .filter(Boolean)
    .map((group) => {
      const alternatives = group
        .split(/\bor\b|\//i)
        .map((part) => part.trim())
        .filter(Boolean);

      const expanded = alternatives.flatMap((part) => {
        const normalizedPart = normalizeToolName(part);
        const alias = aliases[normalizedPart] || [];
        return [part, ...alias];
      });

      return Array.from(new Set(expanded.map((entry) => normalizeToolName(entry)).filter(Boolean)));
    })
    .filter((group) => group.length > 0);
};

const distance = (a = '', b = '') => Math.abs((a || '').length - (b || '').length);

const scoreCandidateMatch = (candidateNormalized, tool) => {
  if (!candidateNormalized) return -1;
  if (tool.normalizedName === candidateNormalized || tool.normalizedSlug === candidateNormalized) return 100;

  const candidateTokens = toTokenSet(candidateNormalized);
  const nameTokens = toTokenSet(tool.normalizedName);
  const slugTokens = toTokenSet(tool.normalizedSlug);

  const allToolTokens = new Set([...nameTokens, ...slugTokens]);
  if (!candidateTokens.size || !allToolTokens.size) return -1;

  let overlap = 0;
  candidateTokens.forEach((token) => {
    if (allToolTokens.has(token)) overlap += 1;
  });

  const tokenCoverage = overlap / candidateTokens.size;
  const tokenRecall = overlap / allToolTokens.size;
  const containsRelation =
    tool.normalizedName.includes(candidateNormalized) ||
    candidateNormalized.includes(tool.normalizedName) ||
    tool.normalizedSlug.includes(candidateNormalized) ||
    candidateNormalized.includes(tool.normalizedSlug);
  const prefixRelation =
    tool.normalizedName.startsWith(candidateNormalized) ||
    candidateNormalized.startsWith(tool.normalizedName) ||
    tool.normalizedSlug.startsWith(candidateNormalized) ||
    candidateNormalized.startsWith(tool.normalizedSlug);

  // Guard against broad accidental matches such as generic single-token candidates.
  if (overlap === 0 && !containsRelation) return -1;

  const score =
    tokenCoverage * 68 +
    tokenRecall * 12 +
    (containsRelation ? 12 : 0) +
    (prefixRelation ? 8 : 0) -
    distance(tool.normalizedName, candidateNormalized) * 0.25;

  return score;
};

export const resolveWorkflowTools = ({ toolStack = [], allToolsCatalog = [], aliases = DEFAULT_ALIASES }) => {
  if (!allToolsCatalog.length || !toolStack.length) return [];

  const resolveToolKey = (tool) => normalizeToolName(tool?.slug || tool?.id || tool?.name || '');

  const catalog = allToolsCatalog.map((tool) => ({
    ...tool,
    normalizedName: normalizeToolName(tool.name || ''),
    normalizedSlug: normalizeToolName(tool.slug || ''),
    resolvedKey: resolveToolKey(tool),
  }));

  const usedKeys = new Set();
  const matched = [];

  const isAlreadyUsed = (tool) => usedKeys.has(tool.resolvedKey || resolveToolKey(tool));

  const markUsed = (tool) => {
    const key = tool.resolvedKey || resolveToolKey(tool);
    if (key) usedKeys.add(key);
  };

  const findBestByCandidate = (candidate) => {
    const candidateNormalized = normalizeToolName(candidate);
    if (!candidateNormalized) return null;

    const exact = catalog.find(
      (tool) =>
        !isAlreadyUsed(tool) &&
        (tool.normalizedName === candidateNormalized || tool.normalizedSlug === candidateNormalized),
    );
    if (exact) return exact;

    // Avoid vague 1-2 character candidates that over-match unrelated tools.
    if (candidateNormalized.length < 3) return null;

    let bestTool = null;
    let bestScore = -1;

    catalog.forEach((tool) => {
      if (isAlreadyUsed(tool)) return;
      const score = scoreCandidateMatch(candidateNormalized, tool);
      if (score > bestScore) {
        bestScore = score;
        bestTool = tool;
      }
    });

    // Require minimum confidence to avoid selecting unrelated tools.
    return bestScore >= 40 ? bestTool : null;
  };

  toolStack.forEach((stackTool) => {
    const groups = extractStepCandidateGroups(stackTool?.name || '', aliases);
    groups.forEach((groupCandidates) => {
      let bestInGroup = null;
      for (const candidate of groupCandidates) {
        const candidateMatch = findBestByCandidate(candidate);
        if (candidateMatch) {
          bestInGroup = candidateMatch;
          break;
        }
      }

      if (bestInGroup && !isAlreadyUsed(bestInGroup)) {
        matched.push(bestInGroup);
        markUsed(bestInGroup);
      }
    });
  });

  return matched;
};
